import { type ReactNode } from 'react';
import { AppShell, AnimatedPage, type NavSection } from '@theonefamily/ui';
import { HubBar } from '@theonefamily/hub-bar';
import { useAuth } from '../../contexts/AuthContext';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import {
  LayoutDashboard,
  Library,
  ClipboardList,
  BarChart3,
  Settings,
  Shield,
  Database,
  Search,
  Download,
  FileText,
  Heart,
} from 'lucide-react';

const NAVIGATION: NavSection[] = [
  {
    label: 'Overview',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, href: '/dashboard' },
      { id: 'legal-health', label: 'Legal Health', icon: Heart, href: '/legal-health' },
    ],
  },
  {
    label: 'Contracts',
    items: [
      { id: 'contracts', label: 'Contract Analysis', icon: FileText, href: '/contracts' },
    ],
  },
  {
    label: 'Policy Management',
    items: [
      { id: 'policy-templates', label: 'Browse Templates', icon: Library, href: '/policies/templates' },
      { id: 'my-policies', label: 'My Policies', icon: ClipboardList, href: '/policies' },
      { id: 'policy-compliance', label: 'Compliance', icon: BarChart3, href: '/policies/compliance' },
    ],
  },
  {
    label: 'eDiscovery',
    items: [
      { id: 'legal-holds', label: 'Legal Holds', icon: Shield, href: '/ediscovery/holds' },
      { id: 'collections', label: 'Collections', icon: Database, href: '/ediscovery/collections' },
      { id: 'review', label: 'Review', icon: Search, href: '/ediscovery/review' },
      { id: 'exports', label: 'Exports', icon: Download, href: '/ediscovery/exports' },
    ],
  },
  {
    label: 'Configuration',
    items: [
      { id: 'settings', label: 'Settings', icon: Settings, href: '/settings' },
    ],
  },
];

interface MspShellProps {
  children: ReactNode;
}

export function MspShell({ children }: MspShellProps) {
  const { user } = useAuth();
  const { isOnline } = useNetworkStatus();

  return (
    <>
      {!isOnline && (
        <div className="sticky top-0 z-50 bg-destructive text-destructive-foreground px-4 py-2 text-center text-sm font-medium">
          You&apos;re offline — changes will sync when reconnected
        </div>
      )}
      <HubBar
          currentProduct="legal"
          apiBase={import.meta.env.VITE_BUS_URL ?? 'https://api.theonestack.com'}
          hubUrl={import.meta.env.VITE_HUB_URL ?? 'https://hub.theonestack.com'}
          supportConfig={{
            apiBaseUrl: import.meta.env.VITE_OPS_CENTER_API_URL ?? 'https://api.theonestack.com',
            apiKey: import.meta.env.VITE_OPS_CENTER_API_KEY ?? '',
            platformId: 'legal',
            platformName: 'The One Legal',
          }}
        />
      <AppShell
        product={{ name: 'The One Legal', accent: '#2563eb' }}
        navigation={NAVIGATION}
        user={{
          name: user?.email ?? 'MSP User',
          email: user?.email ?? '',
          role: user?.role,
        }}
        tenant={user?.tenantSlug ? { name: user.tenantSlug, slug: user.tenantSlug } : undefined}
        onSignOut={() => {
          const hubUrl = import.meta.env.VITE_HUB_URL ?? 'https://hub.theonestack.com';
          window.location.href = hubUrl;
        }}
      >
        <AnimatedPage>{children}</AnimatedPage>
      </AppShell>
    </>
  );
}
